import { useUpdateAppToUserSettings } from 'features/user';
import React, { Suspense } from 'react';
import { Outlet } from 'react-router';
import { ScrollRestoration } from 'react-router-dom';
import { DownloadIosBanner } from 'shared/components/ui/download-ios-banner';
import { CompleteGoalProvider } from 'shared/contexts/complete-goal';
import { CreateGoalTemplateProvider } from 'shared/contexts/create-goal-template';
import { CreateMultipleTasksDialogProvider } from 'shared/contexts/create-multiple-tasks-dialog';
import { DeleteGoalProvider } from 'shared/contexts/delete-goal';
import { DeleteGoalSectionProvider } from 'shared/contexts/delete-goal-section';
import { DeleteHabitProvider } from 'shared/contexts/delete-habit';
import { DeleteLifeAreaProvider } from 'shared/contexts/delete-life-area';
import { DeleteLifeAreaSectionProvider } from 'shared/contexts/delete-life-area-section';
import { DeleteTagProvider } from 'shared/contexts/delete-tag';
import { FeedbackDialogProvider } from 'shared/contexts/feedback-dialog';
import { GoalFormProvider } from 'shared/contexts/goal-form';
import { GoalMetricsFormProvider } from 'shared/contexts/goal-metrics-form';
import { GoalProgressProvider } from 'shared/contexts/goal-progress';
import { GoalProgressDateRangeProvider } from 'shared/contexts/goal-progress-date-range';
import { GoalTemplatePreviewProvider } from 'shared/contexts/goal-template-preview';
import { HabitFormProvider } from 'shared/contexts/habit-form';
import { HabitManualEntryLoggingProvider } from 'shared/contexts/habit-manual-entry-logging';
import { HistoryProvider } from 'shared/contexts/history';
import { InsightsCalculationsProvider } from 'shared/contexts/insights-calculations';
import { LifeAreaFormProvider } from 'shared/contexts/life-area-form';
import { MultiTaskHandlersProvider } from 'shared/contexts/multi-task-handlers';
import { NavigationProvider } from 'shared/contexts/navigation';
import { OnboardingProvider } from 'shared/contexts/onboarding';
import { PremiumDialogProvider } from 'shared/contexts/premium-dialog';
import { RemoveHabitScheduleProvider } from 'shared/contexts/remove-habit-schedule';
import { RequestPushNotificationsProvider } from 'shared/contexts/request-push-notifications';
import { SearchProvider } from 'shared/contexts/search';
import { TagFormProvider } from 'shared/contexts/tag-form';
import { TaskDetailProvider } from 'shared/contexts/task-detail';
import { UserSettingsProvider } from 'shared/contexts/user-settings';
import { VersionProvider } from 'shared/contexts/version';
import { isIos } from 'shared/utils/is-ios';

import { AppLayout } from './components/app-layout';

export const Root: React.FC = () => {
  useUpdateAppToUserSettings();

  return (
    <Suspense>
      <PremiumDialogProvider>
        {isIos() ? (
          <OnboardingProvider>
            <DownloadIosBanner />
          </OnboardingProvider>
        ) : (
          <Suspense>
            <HistoryProvider>
              <RequestPushNotificationsProvider>
                <VersionProvider>
                  <FeedbackDialogProvider>
                    <OnboardingProvider>
                      <InsightsCalculationsProvider>
                        <UserSettingsProvider>
                          <TagFormProvider>
                            <DeleteTagProvider>
                              <DeleteLifeAreaProvider>
                                <DeleteLifeAreaSectionProvider>
                                  <LifeAreaFormProvider>
                                    <CreateGoalTemplateProvider>
                                      <GoalTemplatePreviewProvider>
                                        <CompleteGoalProvider>
                                          <DeleteGoalProvider>
                                            <DeleteGoalSectionProvider>
                                              <GoalFormProvider>
                                                <GoalMetricsFormProvider>
                                                  <GoalProgressProvider>
                                                    <GoalProgressDateRangeProvider>
                                                      <CreateMultipleTasksDialogProvider>
                                                        <RemoveHabitScheduleProvider>
                                                          <DeleteHabitProvider>
                                                            <HabitFormProvider>
                                                              <HabitManualEntryLoggingProvider>
                                                                <AppLayout>
                                                                  <MultiTaskHandlersProvider>
                                                                    <TaskDetailProvider>
                                                                      <SearchProvider>
                                                                        <NavigationProvider>
                                                                          <ScrollRestoration />
                                                                          <Outlet />
                                                                        </NavigationProvider>
                                                                      </SearchProvider>
                                                                    </TaskDetailProvider>
                                                                  </MultiTaskHandlersProvider>
                                                                </AppLayout>
                                                              </HabitManualEntryLoggingProvider>
                                                            </HabitFormProvider>
                                                          </DeleteHabitProvider>
                                                        </RemoveHabitScheduleProvider>
                                                      </CreateMultipleTasksDialogProvider>
                                                    </GoalProgressDateRangeProvider>
                                                  </GoalProgressProvider>
                                                </GoalMetricsFormProvider>
                                              </GoalFormProvider>
                                            </DeleteGoalSectionProvider>
                                          </DeleteGoalProvider>
                                        </CompleteGoalProvider>
                                      </GoalTemplatePreviewProvider>
                                    </CreateGoalTemplateProvider>
                                  </LifeAreaFormProvider>
                                </DeleteLifeAreaSectionProvider>
                              </DeleteLifeAreaProvider>
                            </DeleteTagProvider>
                          </TagFormProvider>
                        </UserSettingsProvider>
                      </InsightsCalculationsProvider>
                    </OnboardingProvider>
                  </FeedbackDialogProvider>
                </VersionProvider>
              </RequestPushNotificationsProvider>
            </HistoryProvider>
          </Suspense>
        )}
      </PremiumDialogProvider>
    </Suspense>
  );
};
